<template>
  <div class="">
    <h3 class="pt-0 mt-0 pb-0">
      {{ $t("viewSupplierPricelistForm.title.add") }}
    </h3>
    <v-toolbar elevation="0" dense rounded class="mt-3">
      <v-toolbar-items>
        <v-btn small text :to="{ name: 'suppliers' }" exact>{{
          $t("viewSuppliers.title")
        }}</v-btn>
        <v-btn small text :to="{ name: 'pricelists' }">
          {{ $t("viewSupplierPricelists.title") }}</v-btn
        >
      </v-toolbar-items>
    </v-toolbar>
    <v-card class="mt-2" elevation="0">
      <v-flex xs12 lg6>
        <v-form ref="supplierPricelistFormStepOne" v-show="currentStep == 1">
          <v-select
            outlined
            :items="suppliers"
            :label="$t('viewSupplierPricelistForm.supplier_name')"
            item-text="name"
            item-value="id"
            v-model="pricelist.supplier_id"
            :rules="[
              (v) => !!v || $t('form.validationErrors.field_is_required'),
            ]"
          ></v-select>
          <v-file-input
            outlined
            accept=".csv,.txt,.xls,.xlsx"
            :label="$t('viewSupplierPricelistForm.select_file_for_uploading')"
            @change="uploadPricelistChange"
            :rules="[
              (v) =>
                (!!v && !!v.name) ||
                $t('form.validationErrors.field_is_required'),
            ]"
          ></v-file-input>
          <v-select
            outlined
            v-if="isShowLimiterSelect"
            :items="delimiters"
            :label="$t('viewSupplierPricelistForm.delimiter')"
            item-text="name"
            item-value="value"
            v-model="pricelist.delimiter"
            :rules="[
              (v) => !!v || $t('form.validationErrors.field_is_required'),
            ]"
          ></v-select>
          <v-select
            outlined
            v-if="pricelistFileParams.filename"
            :items="encodings"
            :label="$t('viewSupplierPricelistForm.encoding')"
            v-model="pricelist.encoding"
            :rules="[
              (v) => !!v || $t('form.validationErrors.field_is_required'),
            ]"
          ></v-select>
          <v-divider></v-divider>
          <v-btn
            class="mt-5"
            elevation="0"
            color="primary"
            @keyup.enter="toStep(2)"
            @click="toStep(2)"
          >
            {{ $t("common.next") }}
          </v-btn>
        </v-form>
      </v-flex>
      <v-flex xs12>
        <v-form ref="supplierPricelistFormStepTwo" v-if="currentStep == 2">
          <v-flex xs12 lg6>
            <v-text-field
              outlined
              lg6
              :label="$t('viewSupplierPricelistForm.start_line')"
              :rules="[
                (v) => !!v || $t('form.validationErrors.field_is_required'),
                (v) =>
                  v >= 1 || $t('form.validationErrors.must_be_greater', [0]),
              ]"
              :value="pricelist.table_config.start_row"
              type="number"
              min="1"
              v-model="pricelist.table_config.start_row"
            ></v-text-field>
          </v-flex>
          <v-flex xs12>
            <h3>
              {{
                $t("viewSupplierPricelistForm.pricelist_fields_configurator")
              }}
            </h3>
            <div class="w-100 overflow-x-auto">
              <table class="price_config_table">
                <thead>
                  <tr>
                    <th
                      v-for="(column, i) in pricelist.table_config.columns"
                      class="pb-2 pt-2"
                    >
                      <div class="w-100 d-flex px-2">
                        <v-btn
                          color="green"
                          depressed
                          x-small
                          outlined
                          fab
                          @click="columnAction('add', i, 'left')"
                          class=""
                          ><v-icon dark
                            >mdi-table-column-plus-before</v-icon
                          ></v-btn
                        >
                        <v-btn
                          v-if="i !== 0"
                          color="blue"
                          depressed
                          x-small
                          outlined
                          fab
                          @click="columnAction('move', i, 'left')"
                          class="ml-1"
                          ><v-icon dark
                            >mdi-format-horizontal-align-left</v-icon
                          ></v-btn
                        >
                        <v-btn
                          v-if="i !== pricelist.table_config.columns.length - 1"
                          color="blue"
                          depressed
                          x-small
                          outlined
                          fab
                          @click="columnAction('move', i, 'right')"
                          class="ml-1"
                          ><v-icon dark
                            >mdi-format-horizontal-align-right</v-icon
                          ></v-btn
                        >
                        <v-btn
                          color="green"
                          depressed
                          x-small
                          outlined
                          fab
                          @click="columnAction('add', i, 'right')"
                          class="ml-1"
                          ><v-icon dark
                            >mdi-table-column-plus-after</v-icon
                          ></v-btn
                        >
                        <v-btn
                          v-if="pricelist.table_config.columns.length > 3"
                          color="red"
                          depressed
                          x-small
                          outlined
                          fab
                          @click="columnAction('remove', i, null)"
                          class="ml-auto"
                          ><v-icon dark>mdi-table-column-remove</v-icon></v-btn
                        >
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td v-for="(column, i) in pricelist.table_config.columns">
                      <v-select
                        v-if="pricelistAvailableColumns"
                        :items="pricelistAvailableColumns"
                        :label="
                          $t('viewSupplierPricelistForm.column_number', [i + 1])
                        "
                        item-text="name"
                        item-value="name"
                        return-object
                        v-model="pricelist.table_config.columns[i]"
                        :rules="[
                          (v) =>
                            !!v ||
                            $t('form.validationErrors.field_is_required'),
                        ]"
                        @change="checkForColumnsDublicates(i)"
                      >
                        <template v-slot:selection="{ parent, item, index }">
                          {{
                            $t(
                              "viewSupplierPricelistForm.pricelist_fields_name." +
                                item.name
                            )
                          }}
                          {{ item.required ? " *" : "" }}
                        </template>
                        <template v-slot:item="{ item, attrs, on }">
                          <v-list-item v-bind="attrs" v-on="on">
                            <v-list-item-content>
                              <v-list-item-title
                                v-text="
                                  $t(
                                    'viewSupplierPricelistForm.pricelist_fields_name.' +
                                      item.name
                                  ) + (item.required ? ' *' : '')
                                "
                              ></v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-select>
                    </td>
                  </tr>
                  <tr v-for="n in 7">
                    <td v-for="column in pricelist.table_config.columns">
                      {{
                        $t(
                          "viewSupplierPricelistForm.pricelist_fields_name." +
                            column.name
                        )
                      }}
                      {{ n }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-flex>
          <v-flex xs12 md6>
            <v-row>
              <v-col class="text-center" cols="12" sm="6">
                <v-select
                  outlined
                  hide-details
                  v-if="pricelistSaleType"
                  :items="pricelistSaleType"
                  :label="$t('viewSupplierPricelistForm.sale_type')"
                  item-text="name"
                  item-value="value"
                  v-model="pricelist.price_sale"
                  :rules="[
                    (v) =>
                      v == 1 ||
                      v == 0 ||
                      $t('form.validationErrors.field_is_required'),
                  ]"
                >
                  <template v-slot:selection="{ parent, item, index }">
                    {{ $t("common." + item.name) }}
                  </template>
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title
                          v-text="$t('common.' + item.name)"
                        ></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col class="text-center" cols="12" sm="6">
                <v-select
                  hide-details
                  outlined
                  v-if="pricelistSaleState"
                  :items="pricelistSaleState"
                  :label="$t('viewSupplierPricelistForm.sale_state')"
                  item-text="name"
                  item-value="value"
                  v-model="pricelist.price_state"
                  :rules="[
                    (v) =>
                      v == 1 ||
                      v == 0 ||
                      $t('form.validationErrors.field_is_required'),
                  ]"
                >
                  <template v-slot:selection="{ parent, item, index }">
                    {{ $t("common." + item.name) }}
                  </template>
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title
                          v-text="$t('common.' + item.name)"
                        ></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col class="text-center" cols="12" sm="6">
                <div class="d-flex">
                  <v-checkbox
                    v-model="showPricelistDelivery"
                    hide-details
                    class="shrink pl-2 mt-0 pb-4"
                    :label="
                      showPricelistDelivery
                        ? ''
                        : $t('viewSupplierPricelistForm.delivery_period')
                    "
                    @change="
                      pricelist.delivery = !showPricelistDelivery
                        ? 0
                        : pricelist.delivery
                    "
                  ></v-checkbox>
                  <v-text-field
                    outlined
                    v-if="showPricelistDelivery"
                    :rules="[
                      (v) =>
                        !!v || $t('form.validationErrors.field_is_required'),
                      (v) =>
                        v >= 1 ||
                        $t('form.validationErrors.must_be_greater', [0]),
                    ]"
                    :value="pricelist.delivery"
                    type="number"
                    class="mb-1 pt-0"
                    v-model="pricelist.delivery"
                    min="1"
                    :label="$t('viewSupplierPricelistForm.delivery_period')"
                  ></v-text-field>
                </div>
              </v-col>
              <v-col class="text-center" cols="12" sm="6">
                <div class="d-flex">
                  <v-checkbox
                    v-model="showPricelistMargin"
                    hide-details
                    class="shrink pl-2 mt-0 pb-4"
                    :label="
                      showPricelistMargin
                        ? ''
                        : $t('viewSupplierPricelistForm.margin')
                    "
                    @change="
                      pricelist.price_margin = !showPricelistMargin
                        ? 0
                        : pricelist.price_margin
                    "
                  ></v-checkbox>
                  <v-text-field
                    outlined
                    v-if="showPricelistMargin"
                    :rules="[
                      (v) =>
                        !!v || $t('form.validationErrors.field_is_required'),
                      (v) =>
                        v >= 1 ||
                        $t('form.validationErrors.must_be_greater', [0]),
                    ]"
                    :value="pricelist.price_margin"
                    v-model="pricelist.price_margin"
                    type="number"
                    min="1"
                    class="mb-1 pt-0"
                    :label="$t('viewSupplierPricelistForm.margin')"
                  >
                    <template v-slot:append> % </template>
                  </v-text-field>
                </div>
              </v-col>
            </v-row>
          </v-flex>
          <template v-if="!requiredFieldsIsFilled">
            <span class="py-2 red--text">{{
              $t("viewSupplierPricelistForm.fill_required_fields")
            }}</span>
          </template>
          <v-divider></v-divider>
          <v-btn
            elevation="0"
            v-if="!isUpdateForm"
            :disabled="savingInProgress"
            :loading="savingInProgress"
            class="mt-5 mr-3"
            color="primary"
            @keyup.enter="toStep(1)"
            @click="toStep(1)"
          >
            {{ $t("common.previous") }}
          </v-btn>
          <v-btn
            elevation="0"
            :disabled="savingInProgress"
            :loading="savingInProgress"
            class="mt-5"
            color="success"
            @keyup.enter="submit()"
            @click="submit()"
          >
            {{ isUpdateForm ? $t("common.save") : $t("common.add") }}
          </v-btn>
        </v-form>
      </v-flex>
    </v-card>
  </div>
</template>
<script>
import { required, email, sameAs, minLength } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";

export default {
  data: () => ({
    zzz: "",
    pricelistIsLoading: false,
    currentStep: 1,
    showPricelistMargin: false,
    showPricelistDelivery: false,
    requiredFieldsIsFilled: true,
    isUpdateForm: false,
  }),
  mounted() {
    this.onLoad();
  },
  created() {},
  validations() {},

  computed: {
    ...mapFields("pricelists", [
      "pricelist",
      "pricelistAvailableColumns",
      "pricelistFileParams",
      "encodings",
      "delimiters",
      "pricelistSaleType",
      "pricelistSaleState",
      "savingInProgress",
    ]),
    ...mapFields("suppliers", ["suppliers", "fetched"]),
    isShowLimiterSelect() {
      return (
        this.pricelistFileParams.extension == "txt" ||
        this.pricelistFileParams.extension == "csv"
      );
    },
  },
  methods: {
    checkForColumnsDublicates(index) {
      let newElement = this.pricelistAvailableColumns.find(
        (item) => item.not_chosen
      );
      for (let i in this.pricelist.table_config.columns) {
        if (
          this.pricelist.table_config.columns[i].name ==
            this.pricelist.table_config.columns[index].name &&
          i != index
        ) {
          this.pricelist.table_config.columns[i] = newElement;
        }
      }
    },
    columnAction(action, index, direction) {
      let newElement = this.pricelistAvailableColumns.find(
        (item) => item.not_chosen
      );
      switch (action) {
        case "add":
          this.pricelist.table_config.columns.splice(
            direction == "right" ? index + 1 : index,
            0,
            newElement
          );
          break;
        case "remove":
          this.pricelist.table_config.columns.splice(index, 1);
          break;
        case "move":
          let firstElement =
            this.pricelist.table_config.columns[
              direction == "right" ? index + 1 : index
            ];
          let secondElement =
            this.pricelist.table_config.columns[
              direction == "right" ? index : index - 1
            ];
          this.pricelist.table_config.columns.splice(
            direction == "right" ? index : index - 1,
            2,
            firstElement,
            secondElement
          );
          break;
      }
    },
    toStep(step) {
      switch (step) {
        case 2:
          if (this.$refs["supplierPricelistFormStepOne"].validate()) {
            this.currentStep = 2;
          }
          break;
        case 1:
          this.currentStep = 1;
          break;
      }
    },
    uploadPricelistClear() {
      this.$refs.uploadPricelistInput.value = "";
      this.uploadPricelistChange();
    },
    uploadPricelistChange(file) {
      if (file) {
        if (file.name) {
          this.pricelistFileParams.filename = file.name;
          this.pricelistFileParams.extension = file.name.split(".").pop();
        }
      } else {
        this.pricelistFileParams.filename = null;
        this.pricelistFileParams.extension = null;
      }
      this.pricelist.file = file;
    },
    onLoad() {
      if (this.$route.params.id > 0) {
        this.currentStep = 2;
        this.isUpdateForm = true;
        this.$store
          .dispatch("pricelists/pricelistShow", { id: this.$route.params.id })
          .then(() => {
            if (this.pricelist === null) {
              this.$router.push({ name: "404" });
            } else {
              this.showPricelistDelivery =
                parseInt(this.pricelist.delivery) > 0 ? true : false;
              this.showPricelistMargin =
                parseInt(this.pricelist.price_margin) > 0 ? true : false;
            }
          });
      } else {
        this.$store.dispatch("suppliers/get");
      }
    },
    submit() {
      let filled = 0;
      let requiredFieldNum = 0;
      for (let field in this.pricelistAvailableColumns) {
        //проверим, что обязательные колонки таблицы указаны
        if (this.pricelistAvailableColumns[field].required) {
          requiredFieldNum++;
          for (let fieldInConfig in this.pricelist.table_config.columns) {
            if (
              this.pricelist.table_config.columns[fieldInConfig].name ===
              this.pricelistAvailableColumns[field].name
            ) {
              filled++;
            }
          }
        }
      }
      this.requiredFieldsIsFilled = filled === requiredFieldNum;

      if (
        this.$refs["supplierPricelistFormStepTwo"].validate() &&
        this.requiredFieldsIsFilled
      ) {
        if (this.$route.params.id) {
          this.$store
            .dispatch("pricelists/pricelistsUpdate", {
              id: this.$route.params.id,
            })
            .then((id) => {
              this.$router.push({ name: "pricelists" });
            });
        } else {
          this.$store.dispatch("pricelists/pricelistsStore").then((id) => {
            if (id > 0) {
              this.$router.push({ name: "pricelists" });
            }
          });
        }
      }
    },
  },
};
</script>
<style lang="css" scoped>
.price_config_table {
  border-collapse: collapse;
}
.price_config_table tr td {
  border: 1px solid red;
  border-color: rgba(0, 0, 0, 0.12);
  padding: 5px;
}
.price_config_table tr:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.12);
}
</style>
